/**
 * cosmetic-atelier theme preset – plastic-surgery / aesthetic-medicine
 * register.
 *
 * First preset in the `cosmetic-X` family (GEN-1963). Per the engineering
 * doc (`docs/engineering/2026-05-13-consona-build-and-cosmetic-atelier-preset.md` §3),
 * this file is a near-copy of `cosmetic.ts` with Consona token deltas
 * applied. **No shared base** – the editorial precedent (verified) is that
 * each preset structurally duplicates its sibling. Fingerprint isolation
 * requires structural duplication.
 *
 * Authority for token values: `packages/themes/src/presets/cosmetic-atelier/DESIGN_SYSTEM.md`
 * §4 (M0.5-reconciled). The DS file declares a 3-tier primitive ramp
 * (Primary navy / Secondary cream-sage / Tertiary gold-brown) with a
 * semantic alias layer. This file ports the **anchor values** from M0.5;
 * unverified primitive stops will be filled in M6 once Studio 3's Styles
 * Page is read via the Figma MCP.
 *
 * Charter hard rules consumed by this preset:
 * - HR-1: Single saturated accent across the page – deep navy `#000044`.
 * - HR-2: Pure white primary surface (`#FFFFFF`); cream `#FAF7F2` is a
 *   secondary tier for alternating sections only.
 * - HR-3: Hero MUST be photographic (video / static / split) – enforced by
 *   `PRESET_COMPOSITION_RULES['cosmetic-atelier']` in M6.
 * - HR-11: Hero text origin is center; meta-row origin bottom-center; scrim
 *   darkens bottom 50% toward navy.
 * - HR-12: Vertical rhythm – `3.5rem` mobile section gap, `6rem` desktop.
 * - HR-13: Sharp 4px card radii; pill 40px button radii. The mixed register
 *   (sharp surfaces + soft action elements) is part of the signature.
 *
 * AI-NOTE: M1 ships a permissive stub. M6 tightens composition rules,
 * locks fingerprints, and may revise photography slot vocabulary. Do NOT
 * widen the hero allow-list past `image-fullbleed-video` /
 * `image-fullbleed-static` / `split` without amending the Charter (HR-3).
 *
 * AI-NOTE: `fontPairings['cormorant-garamond-inter']` is the canonical
 * 2026-04-27 typography pairing (see
 * `docs/engineering/2026-04-27-cosmetic-typography-pairing.md`) and stays the
 * preset default. `fontPairings['newsreader-inter']` (HAL-578) is a second
 * self-hosted option so a second provider on this preset does not share
 * Consona's display serif. All referenced woff2 paths under
 * `/fonts/cosmetic-atelier/...` are provider-relative and land per-provider
 * under `public/fonts/...`; the binaries are vendored at
 * `packages/themes/assets/fonts/cosmetic-atelier/` (see the README there for
 * the provisioning copy step). Earlier M3 builds shipped Libre Baskerville as
 * a fallback while Figma confirmation was pending – that fallback is retired
 * now that the canonical pairing is in place.
 *
 * AI-NOTE: Adding a pairing here is inert unless the provider names it via
 * `provider.theme.fontPairing`. On the unnamed (`default: true`) path the
 * emitted CSS is byte-identical to before HAL-578 for every existing provider.
 */

import { ThemeSchema, type Theme } from '../types';

/**
 * Single serif stack for the whole cosmetic-atelier app. Self-hosted Cormorant
 * Garamond (preloaded below) with a system-serif tail. The tail mirrors the
 * chain ContactEmbed.css uses ("Iowan Old Style" / "Hoefler Text" / Garamond)
 * so the pre-webfont fallback degrades to the same family on every surface.
 */
const COSMETIC_ATELIER_SERIF =
  '"Cormorant Garamond", "Iowan Old Style", "Hoefler Text", Garamond, "Times New Roman", serif';

/**
 * Serif stack for the `newsreader-inter` pairing (HAL-578). Same system-serif
 * tail as `COSMETIC_ATELIER_SERIF` so the pre-webfont state degrades to the
 * same family on both pairings – only the self-hosted head differs.
 */
const COSMETIC_ATELIER_SERIF_NEWSREADER =
  'Newsreader, "Iowan Old Style", "Hoefler Text", Garamond, "Times New Roman", serif';

/**
 * Body / UI sans stack. Shared by both pairings: Inter carries almost no
 * identity signal, so swapping the display serif is what differentiates two
 * providers on this preset (HAL-578).
 */
const COSMETIC_ATELIER_SANS =
  'Inter, "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

export const cosmeticAtelierTheme: Theme = ThemeSchema.parse({
  name: 'cosmetic-atelier',
  colors: {
    // Primary: Deep navy – the brand stake-in-the-ground accent (HR-1).
    // Anchor: M0.5 DS §4 `primary-base` `#000044`. Contrast vs `#FFFFFF`
    // is ~17:1 (AAA).
    primary: '#000044',
    // primaryDark: aliased to the flat brand navy `#000044` (2026-06 brand
    // decision). The cosmetic-atelier dark register moved off the near-black
    // step-down onto a visibly-navy surface; primaryDark drives the dark slab
    // backgrounds (service-cards band, consult card, TriFeature pill) and the
    // deep navy heading-text color, so it leads the move. Kept in lockstep
    // with `colorsP3.primaryDark` below.
    primaryDark: '#000044',
    primaryLight: '#333369',
    // Accent: same navy by design – the cosmetic-atelier register uses ONE
    // saturated accent (HR-1) rather than a primary/secondary pair. Aliasing
    // `accent` to `primary-base` preserves the contract for components that
    // reach for `var(--color-accent)` while keeping the rendered surface
    // monochromatic.
    accent: '#000044',
    // accentDark aliased to brand navy `#000044` in lockstep with primaryDark
    // (HR-1 keeps the accent register monochromatic with primary).
    accentDark: '#000044',
    accentLight: '#333369',
    // Surfaces: pure white primary (HR-2); cream `#FAF7F2` reserved for
    // alternating sections only (DS §4 secondary alias layer).
    background: '#FFFFFF',
    backgroundAlt: '#FAF7F2',
    surface: '#FFFFFF',
    // Text: warm near-black (DS §4 – delta from cosmetic's `#212526`).
    text: '#313130',
    // Muted: deep warm gold (DS §4 tertiary-base). Carries the kicker
    // colorway on light surfaces. Verified ~9.8:1 on white in DS §4.
    textMuted: '#564006',
    textInverse: '#FFFFFF',
    // Border: warm gold tertiary-100 (DS §4). Default hairline rules across
    // the site.
    border: '#6C5008',
    // Mid-warm gold (DS §9.4). Brighter than `border` (`#6C5008`); carries
    // the 93px `EditorialStory` divider hairline and reads as a deliberate
    // brand-mark color in line with the Consona monogram. Preset-scoped: no
    // other preset ships this register, so the token is declared optional in
    // `ThemeColorsSchema` and only emitted as `--color-gold-mid` for
    // cosmetic-atelier (GEN-2029).
    goldMid: '#a78c4a',
    // Status: deepened from cosmetic's defaults so each clears ≥3:1 on
    // white AND remains readable against the cream backgroundAlt.
    success: '#1B6A3F',
    warning: '#9A6204',
    error: '#A92424',
    // Medical-specific (carried from cosmetic; revisited in M6 if the
    // cosmetic-atelier register adopts a divergent evidence palette).
    evidenceStrong: '#1B6A3F',
    evidenceModerate: '#9A6204',
    evidenceLow: '#564006',
    safetyWarning: '#A92424',
    safetyInfo: '#000044',
    // Primary-shade ramp (DS §4 – navy primitive shades). Emits as
    // `--color-primary-shade-{100,200,300}`. shade-100 mirrors primaryDark
    // (already published as `--color-primary-dark`) so authors can reach
    // for either name. shade-200 and shade-300 are the deeper plates used
    // by `TriFeature` and `BeginWithClarityCTA` for their dark slabs.
    // Brand decision (2026-06): the dark slabs use the flat brand navy
    // `#000044` rather than a near-black step-down – the prior near-black
    // ramp (`#00001C` / `#00000E`) read as pure black on-screen, which the
    // brand owner rejected in favor of a visibly navy register. shade-200
    // and shade-300 are therefore aliased up to `#000044`; shade-100 stays
    // at the deeper `#000029` because its only consumer is the footer
    // landscape overlay (photo-dominated, out of scope). For a sibling
    // cosmetic-atelier provider that wants the layered near-black look back,
    // retune these alongside `primary`.
    primaryShade100: '#000029',
    primaryShade200: '#000044',
    primaryShade300: '#000044',
    // Blush pair (DS §9.5) – the "Who should avoid this" safety card surface
    // and its 3px left rule. HAL-464 promoted these from unemitted CSS
    // fallbacks in `EntitySafety.css` to real theme tokens; the values are
    // the exact fallbacks that were rendering, so Consona is unchanged.
    blush: '#f7f1f1',
    blushRule: '#7a4a3a',
    // Cream-gold muted tier for text ON the navy dark slabs (kickers, arrow
    // borders + glyphs, hover fills). HAL-464 promoted this from a
    // section-scoped shim in `TestimonialsCarousel.css` – the value is the
    // same `#c4b99c` that shim declared, so Consona is unchanged.
    textInverseMuted: '#c4b99c',
    // What a photograph's box paints before the image decodes. Pinned to the
    // preset cream rather than left to `backgroundAlt`, because a provider is
    // free to retune `backgroundAlt` for its section rhythm and that must not
    // reach its photography — murrieta's cool #F2F4EE surface read as a grey
    // box behind warm patient portraits. Cream is the value that was already
    // rendering for Consona, so Consona is unchanged.
    imagePlaceholder: '#FAF7F2',
  },
  // NO `colorsP3` block (HAL-464). The preset previously declared Display P3
  // alternates for primary / accent / background / text. `themeToCss()` emits
  // that block AFTER the provider-overridden `:root`, and `getTheme()` never
  // rewrote it, so on any wide-gamut display – i.e. the Mac or iPhone a client
  // reviews their site on – a second cosmetic-atelier provider's brand color
  // silently reverted to Consona navy. The P3 values were the same coordinates
  // re-read in a wider container (`0 0 0.267` is `#000044`), so deleting them
  // costs Consona nothing perceptible and makes the preset reusable by default.
  //
  // `theme.colorsP3` remains a supported schema field. If a future preset
  // declares one, `getTheme()` drops any entry the provider overrides in sRGB
  // so the override can never be clobbered again.
  typography: {
    fontFamily: COSMETIC_ATELIER_SANS,
    // ONE serif for the whole app: self-hosted Cormorant Garamond, per the
    // DESIGN_SYSTEM.md §4/§5 type ramp and HR-6 (Cormorant for all display /
    // heading / italic; Inter for body; weight 400). consona is the only
    // cosmetic-atelier provider. Setting all three serif slots to the same
    // stack makes `themeToCss` emit `--font-family-heading`,
    // `--font-family-display`, and `--font-family-serif` identically, so every
    // component fallback chain converges on the same self-hosted face – home,
    // entity pages, and header/footer. The system-serif tail mirrors
    // ContactEmbed's chain so the local fallback degrades identically before
    // the woff2 (preloaded below) arrives.
    fontFamilyHeading: COSMETIC_ATELIER_SERIF,
    fontFamilyDisplay: COSMETIC_ATELIER_SERIF,
    fontFamilySerif: COSMETIC_ATELIER_SERIF,
    fontWeightHeading: '400',
    fontSizeBase: '1rem',
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.125rem',
    fontSizeXl: '1.25rem',
    fontSize2xl: '1.5rem',
    fontSize3xl: '2.25rem',
    fontSize4xl: '3.25rem',
    lineHeightBase: '1.75',
    lineHeightTight: '1.1',
    lineHeightLoose: '2.14',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    // Sharp card register (HR-13) – 4px on cards / surfaces.
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    // xl reserved for larger cards / panels.
    xl: '1.5rem',
    // Retained for any surface that genuinely wants a capsule (avatar rings,
    // status dots). NOT the CTA radius any more – see `buttonRadius` below.
    full: '9999px',
  },
  // CTA radius (HR-13, amended HAL-618). Squared to 4px, matching
  // `borderRadius.sm` so surfaces and actions share ONE radius and the
  // register reads as deliberately architectural.
  //
  // WHY this is a preset default and not a per-provider opt-in: the preset's
  // rules were reconciled against the shipped Consona build, so every default
  // it carries is, in practice, "look like Consona." The pill was the last
  // shared signature above the fold on the second provider to adopt this
  // preset, and leaving it as the default meant every future provider would
  // re-inherit it and have to discover the same problem again. Flipping the
  // DEFAULT makes differentiation the path of least resistance and the pill
  // the deliberate choice.
  //
  // Consona pins itself back to `9999px` in its own provider.yaml – it is the
  // pill-register exemplar the Charter names, not a provider that forgot to
  // update. Do NOT "clean up" that pin.
  //
  // Consumed by `themeToCssVars()` -> `--button-radius`, which
  // `cta-pill.ts`'s `rounded-[var(--button-radius,9999px)]` reads. The 9999px
  // literal there is a component-level fallback for non-cosmetic-atelier
  // surfaces that never receive the var; it is not this default.
  buttonRadius: '4px',
  shadows: {
    // Low-alpha cool-dark shadows on white (DS §4). Blue undertone keeps
    // shadows from reading as muddy grey against the pure-white surface.
    sm: '0 1px 2px 0 rgb(0 0 41 / 0.04)',
    md: '0 4px 12px -2px rgb(0 0 41 / 0.08)',
    lg: '0 12px 32px -8px rgb(0 0 41 / 0.12)',
    xl: '0 24px 48px -12px rgb(0 0 41 / 0.16)',
  },
  layout: {
    // `heroStyle` is the *legacy* default consumed by the entity-page hero
    // when `hero.variant` is omitted. The cosmetic-atelier flagship hero
    // pattern is `image-fullbleed-video`, but that variant is opted into
    // per-section, not as the preset default. `split` remains the safe
    // baseline for entity hero rendering.
    heroStyle: 'split',
    cardStyle: 'minimal',
    iconStyle: 'geometric',
    buttonStyle: 'pill',
    sectionPadding: '4rem',
    contentMaxWidth: '65ch',
  },
  // Motion tokens – cosmetic-atelier Motion Hard Rules (DS §6).
  // Stillness invariant: the register reads as still despite the video hero.
  // Reduced-motion compliance is honored by the global reset emitted via
  // `themeToCss()`.
  motion: {
    durationInstant: '0ms',
    durationFast: '150ms',
    durationNormal: '250ms',
    durationModerate: '400ms',
    // Cinematic – hero image / video fade-in (DS §6).
    durationEditorial: '800ms',
    easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
    easeIn: 'cubic-bezier(0.55, 0, 1, 0.45)',
    easeInOut: 'cubic-bezier(0.45, 0, 0.55, 1)',
    // Standard ease for the long beats (section reveals, hero fade-ins).
    easeEditorial: 'cubic-bezier(0.22, 1, 0.36, 1)',
    // No transform exceeds 16px on a single beat (DS §6).
    distanceShort: '4px',
    distanceMedium: '8px',
    distanceLong: '16px',
    // No parallax in the cosmetic-atelier register (DS §6 stillness invariant).
    parallaxMax: '0%',
  },
  // Density tokens align with PRESET_COMPOSITION_RULES['cosmetic-atelier']
  // and the DS file §4 density block. `homeMaxSections` is the M1 stub
  // value (10) – M6 may raise to the DS-file ceiling (14) once the actual
  // shipped Consona section count is known.
  density: {
    homeSectionGap: '3.5rem',
    homeSectionGapDesktop: '6rem',
    homeMaxSections: 10,
    contentMaxWidthDisplay: '22ch',
    contentMaxWidthLede: '48ch',
    contentMaxWidthProse: '68ch',
    contentMaxWidthEditorial: '90rem',
    contentMaxWidthFullBleed: '100vw',
    gutterMobile: '1.5rem',
    gutterDesktop: '4rem',
  },
  // Hero CSS hooks – cosmetic-atelier's photographic-centered register
  // (DS §4 + §5 HR-11). Differs from cosmetic's `top-center` mark origin
  // and bottom-anchored scrim.
  hero: {
    aspectMobile: '4 / 5',
    aspectDesktop: '16 / 9',
    markOrigin: 'top-center',
    textOrigin: 'center',
    // Scrim darkens the bottom 50% toward navy `#000029` so the white
    // meta-row links stay legible.
    overlayScrim: 'linear-gradient(180deg, rgba(0, 0, 41, 0) 50%, rgba(0, 0, 41, 0.4) 100%)',
    // Cinematic fade-in (DS §4 hero block).
    imageFadeDuration: '800ms',
  },
  // Named font pairings. Each self-hosts Inter (body) plus one display serif
  // (display/heading/italic) via `@font-face`. Two are registered:
  //
  // - `cormorant-garamond-inter` (`default: true`) – the canonical 2026-04-27
  //   pairing. Selected whenever a provider names no pairing, which is every
  //   provider shipped today, so this stays the preset's rendered default.
  // - `newsreader-inter` (HAL-578) – second self-hosted option. Typography is
  //   the strongest signal a viewer uses to decide whether two sites are the
  //   same site, so a second provider on this preset needs a display serif
  //   Consona is not already using. Inter is deliberately shared: it carries
  //   almost no identity signal, and swapping it would add ramp risk for no
  //   differentiation gain.
  //
  // Providers select via `provider.theme.fontPairing`. An explicit selection
  // also repoints `--font-family-{heading,display,serif}` onto that pairing's
  // families and suppresses the Google Fonts `<link>`, so opting in never
  // reaches the CDN (HR-6). See `getTheme()` in `packages/themes/src/index.ts`.
  //
  // Per the Performance Budget (B6 §2) + DS §4/HR-6, every pairing preloads
  // exactly TWO display-serif faces with `font-display: swap` – the dominant
  // H1–H6 heading weight (400 normal) and the hero italic conviction span
  // (400 italic, HR-0). Inter (body) stays `font-display: optional` so body
  // text never reflows.
  fontPairings: {
    'cormorant-garamond-inter': {
      id: 'cormorant-garamond-inter',
      label: 'Cormorant Garamond + Inter',
      default: true,
      // Live config when this pairing is selected by name (HAL-578): `getTheme()`
      // copies these onto `theme.typography`. On the `default: true`
      // fallthrough path they are not read, and `theme.typography` above
      // already names the same Cormorant stack – so the two agree either way.
      fontFamilyDisplay: COSMETIC_ATELIER_SERIF,
      fontFamilyHeading: COSMETIC_ATELIER_SERIF,
      fontFamilyBody: COSMETIC_ATELIER_SANS,
      fontFamilyUi: COSMETIC_ATELIER_SANS,
      fontWeightDisplay: '400',
      fontWeightHeading: '400',
      fontWeightBody: '400',
      fontWeightUi: '400',
      trackingDisplay: '-0.04em',
      trackingHeading: '-0.02em',
      trackingBody: '0',
      // Uppercase kicker tracking (DS §4 typography ramp – 17px on 14px).
      trackingLabel: '0.286em',
      // Wordmark tracking (DS §4 typography ramp – 14px / 0.357em – matches
      // the button-label tracking for cohesion).
      trackingWordmark: '0.357em',
      files: [
        // Cormorant 400 italic – the hero/conviction italic span (HR-0).
        // Preloaded + swap so the italic paints in Cormorant on first load.
        {
          family: 'Cormorant Garamond',
          weight: '400',
          style: 'italic',
          url: '/fonts/cosmetic-atelier/cormorant-garamond/cormorant-garamond-400-italic.woff2',
          format: 'woff2',
          preload: true,
          display: 'swap',
        },
        // Body regular (Inter Variable – covers 100..900 in a single file).
        {
          family: 'Inter',
          weight: '100 900',
          style: 'normal',
          url: '/fonts/cosmetic-atelier/inter/Inter-Variable-latin.woff2',
          format: 'woff2-variations',
          preload: false,
          display: 'optional',
        },
        // Cormorant Garamond 400 regular – the dominant H1–H6 heading weight.
        // consona's globals.css maps headings to var(--font-family-heading),
        // which now resolves to the Cormorant stack. Preloaded + swap.
        {
          family: 'Cormorant Garamond',
          weight: '400',
          style: 'normal',
          url: '/fonts/cosmetic-atelier/cormorant-garamond/cormorant-garamond-400.woff2',
          format: 'woff2',
          preload: true,
          display: 'swap',
        },
        // Dormant: Cormorant Garamond 500 (the type ramp uses weight 400 only).
        {
          family: 'Cormorant Garamond',
          weight: '500',
          style: 'normal',
          url: '/fonts/cosmetic-atelier/cormorant-garamond/cormorant-garamond-500.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
      ],
    },
    // HAL-578. Newsreader was chosen over Fraunces and Spectral substantially
    // because its italic is properly drawn rather than a slanted roman
    // (`post.italicAngle` -17, structurally different outlines), which the
    // hero + final-CTA conviction spans depend on (HR-0).
    //
    // Binaries: `packages/themes/assets/fonts/cosmetic-atelier/{newsreader,inter}/`
    // (static latin-subset woff2, OFL-1.1). Provisioning copies BOTH
    // directories into the adopting provider's `public/fonts/cosmetic-atelier/`;
    // the URLs below are provider-relative and resolve there at runtime.
    // Copying only the serif leaves body text on the system sans, and because
    // Inter is `preload: false` the font-preload gate does not catch it.
    //
    // Weight 400 only, matching the type ramp – there is no Newsreader
    // equivalent of the dormant Cormorant 500, because nothing consumes it.
    'newsreader-inter': {
      id: 'newsreader-inter',
      label: 'Newsreader + Inter',
      fontFamilyDisplay: COSMETIC_ATELIER_SERIF_NEWSREADER,
      fontFamilyHeading: COSMETIC_ATELIER_SERIF_NEWSREADER,
      fontFamilyBody: COSMETIC_ATELIER_SANS,
      fontFamilyUi: COSMETIC_ATELIER_SANS,
      // Ramp tokens are identical to the Cormorant pairing on purpose: the DS
      // §4 type ramp (52px display, -2px display tracking, 17px kicker
      // tracking on 14px) is one scale shared by both pairings. Newsreader has
      // a larger x-height than Cormorant, so display sizes may read heavier at
      // the same px value; if the hero H1 needs adjusting, retune
      // `trackingDisplay` here rather than changing the size stops, so the two
      // pairings stay on one scale. Deferred until something renders it.
      fontWeightDisplay: '400',
      fontWeightHeading: '400',
      fontWeightBody: '400',
      fontWeightUi: '400',
      trackingDisplay: '-0.04em',
      trackingHeading: '-0.02em',
      trackingBody: '0',
      trackingLabel: '0.286em',
      trackingWordmark: '0.357em',
      files: [
        // Newsreader 400 italic – the hero/conviction italic span (HR-0).
        {
          family: 'Newsreader',
          weight: '400',
          style: 'italic',
          url: '/fonts/cosmetic-atelier/newsreader/newsreader-400-italic.woff2',
          format: 'woff2',
          preload: true,
          display: 'swap',
        },
        // Body regular (Inter Variable – covers 100..900 in a single file).
        // Same file the Cormorant pairing references; a provider adopting
        // either pairing ships one Inter binary.
        {
          family: 'Inter',
          weight: '100 900',
          style: 'normal',
          url: '/fonts/cosmetic-atelier/inter/Inter-Variable-latin.woff2',
          format: 'woff2-variations',
          preload: false,
          display: 'optional',
        },
        // Newsreader 400 regular – the dominant H1–H6 heading weight.
        {
          family: 'Newsreader',
          weight: '400',
          style: 'normal',
          url: '/fonts/cosmetic-atelier/newsreader/newsreader-400.woff2',
          format: 'woff2',
          preload: true,
          display: 'swap',
        },
      ],
    },
  },
});
